import { dateUStoISO } from '../../date-converter';
import { formatName } from '../../utils';

// What Bulk Edit can set, and how each one is edited.
//
// Deliberately not every PROPERTIES column: this is the set that is worth
// setting the same way on a batch of properties. The figures that are read off
// one property at a time — a price, an address, the agent splits — stay on the
// property panel, where you can see what you are overwriting.
//
// `control` picks the editor; `group` only groups the picker's options.
//
// `id` identifies the entry, `code` the column it writes, and they are the same
// for all but the industrial subcategories: industrial properties keep their
// subcategory in the ordinary tenant-subcategory columns, only from a different
// list of options, so two entries share one column. Nothing may pick both — see
// buildAttributes.
export const FIELDS = [
    // ------------------------------------------------------ the property
    {
        code: 'NAME',
        label: 'Property Name',
        group: 'Property',
        control: 'text',
    },
    {
        // The appraisal district's page for the parcel — one link per county
        // scheme, so a batch from the same county shares it.
        code: 'CAD_SITE',
        label: 'CAD Site',
        group: 'Property',
        control: 'text',
    },

    // ---------------------------------------------------- classification
    {
        code: 'STATUS',
        label: 'Status',
        group: 'Status & Type',
        control: 'dropdown',
        optionsType: 'status',
        optionKey: 'code',
    },
    {
        code: 'HSC_STATUS',
        label: 'Financing Status (PEAK)',
        group: 'Status & Type',
        control: 'dropdown',
        optionsType: 'status-financing',
    },
    {
        code: 'SOURCE',
        label: 'Source',
        group: 'Status & Type',
        control: 'dropdown',
        optionsType: 'source',
    },
    {
        code: 'TRANSACTION_TYPE',
        label: 'Transaction Type',
        group: 'Status & Type',
        control: 'dropdown',
        optionsType: 'transaction',
    },
    {
        code: 'CATEGORY',
        label: 'Category',
        group: 'Status & Type',
        control: 'dropdown',
        optionsType: 'category',
        optionKey: 'code',
        optionLabelKey: 'name',
    },
    {
        code: 'TYPE',
        label: 'Type',
        group: 'Status & Type',
        control: 'dropdown',
        optionsType: 'type',
        optionKey: 'code',
        optionLabelKey: 'name',
    },
    {
        code: 'SINGLE_TENANT_SUBCATEGORY',
        label: 'Single-Tenant Subcategory',
        group: 'Status & Type',
        control: 'dropdown',
        optionsType: 'subcategorySTNL',
    },
    {
        code: 'MULTI_TENANT_SUBCATEGORY',
        label: 'Multi-Tenant Subcategory',
        group: 'Status & Type',
        control: 'dropdown',
        optionsType: 'subcategoryMT',
    },
    {
        // Industrial properties keep their subcategory in the same two columns
        // as every other property; only the list of options differs (see
        // getTenantSubcategorySTNL in utils). A bulk edit spans properties of
        // more than one category, so which list applies cannot be worked out
        // here — the user picks the list by picking the field.
        id: 'SINGLE_TENANT_SUBCATEGORY_INDUSTRIAL',
        code: 'SINGLE_TENANT_SUBCATEGORY',
        label: 'Industrial Subcategory (STNL)',
        group: 'Status & Type',
        control: 'dropdown',
        optionsType: 'subcategoryIndustrialSTNLMT',
    },
    {
        id: 'MULTI_TENANT_SUBCATEGORY_INDUSTRIAL',
        code: 'MULTI_TENANT_SUBCATEGORY',
        label: 'Industrial Subcategory (MT)',
        group: 'Status & Type',
        control: 'dropdown',
        optionsType: 'subcategoryIndustrialSTNLMT',
    },
    {
        code: 'SUB_CATEGORY',
        label: 'Sub Category (Multi-Family)',
        group: 'Status & Type',
        control: 'dropdown',
        optionsType: 'subcategory',
        optionKey: 'code',
        optionLabelKey: 'name',
    },
    {
        code: 'LEASE_TYPE',
        label: 'Lease Type',
        group: 'Status & Type',
        control: 'dropdown',
        optionsType: 'lease',
    },

    // ---------------------------------------------------------- location
    {
        code: 'MARKET',
        label: 'Market',
        group: 'Location',
        control: 'dropdown',
        optionsType: 'market',
        optionKey: 'code',
        optionLabelKey: 'name',
    },
    {
        code: 'CITY',
        label: 'City',
        group: 'Location',
        control: 'text',
    },
    {
        code: 'STATE',
        label: 'State',
        group: 'Location',
        control: 'dropdown',
        optionsType: 'states',
        optionKey: 'abbr',
        optionLabelKey: 'name',
    },
    {
        code: 'ZIP',
        label: 'Zip',
        group: 'Location',
        control: 'text',
    },
    {
        code: 'COUNTY',
        label: 'County',
        group: 'Location',
        control: 'text',
    },

    // ------------------------------------------------------------ people
    {
        // The property's owner, as on the Investors tab — an ownership period,
        // not a column, so it is posted separately (see ownerId below).
        code: 'CURRENT_OWNER_ID',
        label: 'Investor (Owner)',
        group: 'People',
        control: 'investor',
    },
    {
        code: 'BUYER_ID',
        label: 'Buyer',
        group: 'People',
        control: 'buyer',
    },
    {
        code: 'SELLER_ID',
        label: 'Seller',
        group: 'People',
        control: 'seller',
    },
    {
        code: 'LENDER_ID',
        label: 'Lender',
        group: 'People',
        control: 'lender',
    },

    // ------------------------------------------------------------ agents
    // Summit's own agents on each side, and the firm each side's outside agent
    // belongs to — the same pickers the Listing Agent and Buyside Agent tabs
    // use. The percentage splits stay on those tabs: they are per-deal figures,
    // not something to write across a batch.
    {
        code: 'LISTING_AGENT_1',
        label: 'Listing Agent 1',
        group: 'Agents',
        control: 'team',
    },
    {
        code: 'LISTING_AGENT_2',
        label: 'Listing Agent 2',
        group: 'Agents',
        control: 'team',
    },
    {
        code: 'LISTING_AGENT_3',
        label: 'Listing Agent 3',
        group: 'Agents',
        control: 'team',
    },
    {
        // "Outside Agent" on the Listing Agent tab — someone outside Summit,
        // so a different list from the three above.
        code: 'LISTING_AGENT_4',
        label: 'Listing Outside Agent',
        group: 'Agents',
        control: 'agent',
    },
    {
        code: 'LISTING_AGENT_4_FIRM',
        label: 'Listing Agent Firm',
        group: 'Agents',
        control: 'firm',
    },
    {
        code: 'BUYER_AGENT_1',
        label: 'Buyer Agent 1',
        group: 'Agents',
        control: 'team',
    },
    {
        code: 'BUYER_AGENT_2',
        label: 'Buyer Agent 2',
        group: 'Agents',
        control: 'team',
    },
    {
        code: 'BUYER_AGENT_3',
        label: 'Buyer Agent 3',
        group: 'Agents',
        control: 'team',
    },
    {
        code: 'BUYER_AGENT_4',
        label: 'Buyside Outside Agent',
        group: 'Agents',
        control: 'agent',
    },
    {
        code: 'BUYER_AGENT_4_FIRM',
        label: 'Buyer Agent Firm',
        group: 'Agents',
        control: 'firm',
    },
    {
        // The referring agent and their firm are a row in
        // REFERRING_OUTSIDE_AGENT rather than columns on the property, so they
        // are posted separately — see referringAttributes below.
        code: 'REFERRING_BUYER_AGENT',
        label: 'Buyside Referring Agent',
        group: 'Agents',
        control: 'agent',
        table: 'referring',
    },
    {
        code: 'REFERRING_BUYER_AGENT_FIRM',
        label: 'Buyside Referring Agent Firm',
        group: 'Agents',
        control: 'firm',
        table: 'referring',
    },

    // ------------------------------------------------------------- dates
    {
        code: 'LISTED_DATE',
        label: 'Date Listed',
        group: 'Dates',
        control: 'date',
    },
    {
        code: 'CLOSE_OF_ESCROW',
        label: 'Close of Escrow',
        group: 'Dates',
        control: 'date',
    },
    {
        code: 'LEASE_COMMENCEMENT_DATE',
        label: 'Lease Commencement Date',
        group: 'Dates',
        control: 'date',
    },
    {
        code: 'LEASE_EXPIRATION_DATE',
        label: 'Lease Expiration Date',
        group: 'Dates',
        control: 'date',
    },

    // ------------------------------------------------------------- flags
    {
        code: 'VISIBLE',
        label: 'Visible',
        group: 'Flags',
        control: 'yesno',
    },
    {
        code: 'FEATURED',
        label: 'Featured',
        group: 'Flags',
        control: 'yesno',
    },
    {
        code: 'CORE_PROPERTY',
        label: 'Core Property',
        group: 'Flags',
        control: 'yesno',
    },
    {
        code: 'NEED_INFO',
        label: 'Need Info',
        group: 'Flags',
        control: 'yesno',
    },
    {
        code: 'HIDE_FINANCIALS',
        label: 'Hide Financials',
        group: 'Flags',
        control: 'yesno',
    },

    // ---------------------------------------------------------- building
    {
        code: 'SQFT',
        label: 'Square Feet',
        group: 'Building',
        control: 'number',
    },
    {
        code: 'ACRES',
        label: 'Lot Size (acres)',
        group: 'Building',
        control: 'number',
    },
    {
        code: 'UNITS',
        label: '# of Units',
        group: 'Building',
        control: 'number',
    },
    {
        code: 'YEAR_BUILT',
        label: 'Year Built',
        group: 'Building',
        control: 'number',
    },
];

/** What identifies a catalogue entry: its own id, or the column it writes. */
export function fieldId(field) {
    return field.id || field.code;
}

export const FIELDS_BY_ID = FIELDS.reduce((map, field) => {
    map[fieldId(field)] = field;
    return map;
}, {});

/**
 * How many properties travel in one request — mirrors
 * BulkUpdatePropertiesRequest::MAX_PROPERTIES.
 *
 * A selection larger than this is posted a chunk at a time rather than
 * refused, so the size of the edit is a matter of how long the user waits
 * rather than a wall at the end of the first page of the list.
 */
export const CHUNK_SIZE = 500;

/**
 * The largest edit offered in one go.
 *
 * Matched to the mail merge cap, and to what the list itself will hand over
 * (see Listing's fetchFullFilteredSet) — past this the honest answer is to
 * narrow the filter rather than to sit through twenty round trips.
 */
export const MAX_PROPERTIES = 5000;

/** The one chosen field that is not a column of any kind. */
export const OWNER_FIELD = 'CURRENT_OWNER_ID';

/** Every chosen field that writes REFERRING_OUTSIDE_AGENT rather than PROPERTIES. */
export function isReferringField(field) {
    return Boolean(field) && field.table === 'referring';
}

const CLEARED = '';

function stripFormatting(value) {
    return String(value).replace(/[$,\s]/g, '');
}

/**
 * The PROPERTIES column => value map to post.
 *
 * A field the user added but left blank is sent as an empty string, which the
 * server reads as "clear this column" — the same contract a property save has.
 * Throws for a date that is not MM/DD/YYYY, so the user is told which field
 * rather than the server rejecting the whole edit.
 */
export function buildAttributes(chosen, values) {
    const attributes = {};

    chosen.forEach((id) => {
        const field = FIELDS_BY_ID[id];
        if (!field || field.code === OWNER_FIELD || isReferringField(field)) return;

        // Two entries can write one column — Tenant Subcategory and its
        // industrial list — and a batch cannot be given both.
        if (Object.prototype.hasOwnProperty.call(attributes, field.code)) {
            throw new Error(
                `${field.label} writes the same column as another field you chose. Remove one of them.`,
            );
        }

        const raw = values[id];
        const value = raw === null || raw === undefined ? '' : String(raw).trim();

        if (value === '') {
            attributes[field.code] = CLEARED;
            return;
        }

        switch (field.control) {
            case 'date': {
                const iso = dateUStoISO(value);
                if (iso === false) {
                    throw new Error(`${field.label} must be a date in MM/DD/YYYY format.`);
                }
                attributes[field.code] = iso;
                break;
            }
            // The app stores these as 'y' or nothing at all — there is no 'n'
            // in PROPERTIES — so "No" is a clear.
            case 'yesno':
                attributes[field.code] = value === 'y' ? 'y' : CLEARED;
                break;
            case 'number': {
                const number = stripFormatting(value);
                if (number === '' || isNaN(Number(number))) {
                    throw new Error(`${field.label} must be a number.`);
                }
                attributes[field.code] = number;
                break;
            }
            default:
                attributes[field.code] = value;
        }
    });

    return attributes;
}

/**
 * The REFERRING_OUTSIDE_AGENT column => value map to post, or an empty map.
 *
 * Blank clears, as it does for a column: the referring agent comes off the
 * deal. The split percentage beside them is not offered here — who referred a
 * deal is the same answer across a batch, what they are owed is not.
 */
export function referringAttributes(chosen, values) {
    const referring = {};

    chosen.forEach((id) => {
        const field = FIELDS_BY_ID[id];
        if (!isReferringField(field)) return;

        const raw = values[id];
        referring[field.code] = raw === null || raw === undefined ? CLEARED : String(raw).trim();
    });

    return referring;
}

/**
 * The investor every selected property should be handed to, or null.
 *
 * Unlike a column, this one cannot be blank: an ownership period with nobody
 * in it is not a thing, and "clear the owner" is a different, destructive
 * operation that belongs on the property's own Investors tab.
 */
export function ownerId(chosen, values) {
    if (chosen.indexOf(OWNER_FIELD) === -1) return null;

    const value = values[OWNER_FIELD];
    const id = value === null || value === undefined ? '' : String(value).trim();

    if (id === '') {
        throw new Error('Choose an investor for Investor (Owner), or remove that field.');
    }

    return Number(id);
}

/** What the confirmation step shows for one field. */
export function describeValue(id, values, labels) {
    const raw = values[id];

    if (raw === null || raw === undefined || String(raw).trim() === '') {
        return { text: 'Cleared', cleared: true };
    }

    return { text: labels[id] || String(raw), cleared: false };
}

/**
 * A label for whatever the dropdown handed back, for the confirmation step —
 * the value itself is an id or a code the user never sees.
 *
 * Buyers, sellers and lenders are shown the way their own dropdown shows them
 * (see Dropdown's getOptionLabel), so the confirmation names the same person
 * the user just picked.
 */
export function optionLabel(option, fallback) {
    if (option === null || option === undefined) return fallback;
    if (typeof option === 'string') return option;

    const personName = formatName(option);
    if (personName) {
        const firm = option.FIRM_NAME && option.FIRM_NAME.trim();
        return firm ? `${personName} - ${firm}` : personName;
    }

    return option.name || option.label || option.code || option.value || fallback;
}
